import React, { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { useDashTheme } from '../../DashboardV2/hooks/useDashTheme';
import { SectionCard, Avatar, Badge } from '../../DashboardV2/components/primitives';
import { getLeaderboard } from '../../../services/goalsPerformanceApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useGoalsSettings } from '../../../contexts/GoalsSettingsContext';

// Medal tints for the top three ranks; everyone else gets the muted chip.
const MEDALS = {
  1: { bg: '#EECC1633', fg: '#8a6d00' },
  2: { bg: '#96ACA733', fg: '#5b6b67' },
  3: { bg: '#F59E9633', fg: '#8a3d36' },
};

// Company-wide scoreboard: top 5 by average cycle score + where you stand.
// Reuses the existing /leaderboard endpoint (it computes every user anyway),
// so rank is derived client-side from one fetch.
const GoalsScoreboard = ({ maxScore = 10 }) => {
  const t = useDashTheme();
  const { user } = useAuth();
  const { settings: goalsSettings } = useGoalsSettings();

  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);

  const flowMode = goalsSettings?.goalsMode || 'classic';
  const myId = (user?.id || user?._id)?.toString();

  useEffect(() => {
    if (flowMode === 'checklist') return; // no scores to rank
    let cancelled = false;
    getLeaderboard(100)
      .then(res => { if (!cancelled && res?.success) setBoard(res.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [flowMode]);

  // Checklist orgs have no scores — a scoreboard would always be empty.
  if (flowMode === 'checklist') return null;

  const topScore = board[0]?.averageScore || maxScore;
  const top5 = board.slice(0, 5);
  const myEntry = board.find(e => e.userId?.toString() === myId) || null;
  const myInTop5 = !!top5.find(e => e.userId?.toString() === myId);

  const Row = ({ entry, isMe }) => {
    const medal = MEDALS[entry.rank];
    const barPct = topScore > 0 ? Math.max(4, Math.round((entry.averageScore / topScore) * 100)) : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: t.s[2], padding: `${t.s[2]}px 0` }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, borderRadius: 99, flexShrink: 0,
          fontSize: 10, fontWeight: t.font.weight.semibold, fontVariantNumeric: 'tabular-nums',
          background: medal ? medal.bg : t.c.surfaceMuted,
          color: medal ? medal.fg : t.c.textTertiary,
        }}>
          {entry.rank}
        </span>
        <Avatar user={entry} name={`${entry.firstName || ''} ${entry.lastName || ''}`.trim()} size={24} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: t.font.size.sm, fontWeight: isMe ? t.font.weight.semibold : t.font.weight.medium,
              color: t.c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {entry.firstName} {entry.lastName}
            </span>
            {isMe && <Badge tone="brand">You</Badge>}
          </div>
          <div style={{ marginTop: 3, height: 3, borderRadius: 99, background: t.c.surfaceMuted, overflow: 'hidden' }}>
            <div style={{
              width: `${barPct}%`, height: '100%', borderRadius: 99,
              background: medal ? (entry.rank === 1 ? '#EECC16' : entry.rank === 2 ? '#96ACA7' : '#F59E96') : t.c.primary,
              transition: 'width 400ms ease',
            }} />
          </div>
        </div>
        <span style={{ fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, color: t.c.text, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {entry.averageScore}
          <span style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.regular, color: t.c.textTertiary }}>/{maxScore}</span>
        </span>
      </div>
    );
  };

  return (
    <SectionCard title="Company Scoreboard" icon={<Trophy size={15} />} accent="yellow">
      <div>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[2] }}>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="animate-pulse" style={{ height: 32, borderRadius: t.r.md, background: t.c.surfaceMuted }} />
            ))}
          </div>
        ) : board.length === 0 ? (
          <div style={{ fontSize: t.font.size.base, color: t.c.textSecondary, padding: `${t.s[4]}px 0`, textAlign: 'center' }}>
            No scores on the board yet — be the first!
          </div>
        ) : (
          <>
            {top5.map(entry => (
              <Row key={entry.userId} entry={entry} isMe={entry.userId?.toString() === myId} />
            ))}
            {myEntry && !myInTop5 && (
              <>
                <div style={{ borderTop: `1px dashed ${t.c.border}`, margin: `${t.s[1]}px 0` }} />
                <Row entry={myEntry} isMe />
              </>
            )}
            {!myEntry && (
              <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, paddingTop: t.s[2] }}>
                Get your goals approved and scored to join the board.
              </div>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
};

export default GoalsScoreboard;
